import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail, Loader2, Send } from "lucide-react";

interface EmailComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipientEmail: string;
  recipientName: string;
  customerId?: string | null;
  defaultSubject?: string;
  onSent?: () => void;
}

export function EmailComposeDialog({
  open,
  onOpenChange,
  recipientEmail,
  recipientName,
  customerId,
  defaultSubject = "",
  onSent,
}: EmailComposeDialogProps) {
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setSubject(defaultSubject);
      setBody("");
    }
    onOpenChange(isOpen);
  };

  const handleSend = async () => {
    if (!recipientEmail) {
      toast.error("Recipient has no email address");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    if (!body.trim()) {
      toast.error("Message body is required");
      return;
    }

    setIsSending(true);
    try {
      const { data: userData } = await supabase.auth.getUser();

      // Send via Resend edge function
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          type: "outbound",
          data: {
            to: recipientEmail,
            subject: subject.trim(),
            body: body.trim(),
            recipientName,
          },
        },
      });

      if (error) throw error;

      // Log to messages table for communication history
      if (customerId) {
        await supabase.from("messages").insert({
          customer_id: customerId,
          direction: "outbound",
          channel: "email",
          body: `Subject: ${subject.trim()}\n\n${body.trim()}`,
          status: "sent",
          sent_by: userData?.user?.id || null,
          to_number: recipientEmail,
        } as any);
      }

      toast.success(`Email sent to ${recipientName}`);
      setSubject("");
      setBody("");
      onOpenChange(false);
      onSent?.();
    } catch (err: unknown) {
      console.error(err);
      const message = err instanceof Error ? err.message : "Failed to send email";
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Compose Email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* From */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground w-12">From:</span>
            <Badge variant="outline" className="font-normal">Car Street &lt;info@carstreet.ca&gt;</Badge>
          </div>

          {/* To */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground w-12">To:</span>
            {recipientEmail ? (
              <Badge variant="outline" className="font-normal">
                {recipientName} &lt;{recipientEmail}&gt;
              </Badge>
            ) : (
              <Badge variant="destructive">No email address</Badge>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-1.5">
            <Label htmlFor="email-subject" className="text-sm">Subject *</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
            />
          </div>

          {/* Body */}
          <div className="space-y-1.5">
            <Label htmlFor="email-body" className="text-sm">Message *</Label>
            <Textarea
              id="email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your message..."
              rows={8}
              className="resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={isSending || !recipientEmail || !subject.trim() || !body.trim()}
            >
              {isSending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send Email</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
